// ds-color-ignore-file: dev-only / internal tooling surface
import type { EditTarget } from "./resolve-edit-target"
import type { MessageIndex } from "@/scripts/build-message-index"
import { isLargeString } from "../review/locate-state"
import { elementText } from "./pick-text-target"

/**
 * Whether a picked element should be editable in place (contentEditable) rather
 * than panel-only. Inline only for short, plain, unambiguous strings:
 *  - intl: an exact key, not a `{placeholder}` template, whose on-page (focus-
 *    locale) value isn't "large".
 *  - cms: a non-lexical (scalar/html) field whose on-page text isn't "large".
 *  - the element is a plain text node (no child elements) so contentEditable
 *    edits pure text, never an icon/link/rich-span mix.
 * Anything else (ambiguous, template, lexical, large, mixed node, null) → false,
 * i.e. the existing panel-only flow.
 */
export function isInlineEligible(
  el: HTMLElement,
  target: EditTarget,
  index: MessageIndex | null,
  focusLocale: string
): boolean {
  if (!target) return false
  // Pure text node only — no nested elements (icons, links, <b> runs).
  if (el.children.length > 0) return false
  if (target.kind === "cms") {
    if (target.cms.kind === "lexical") return false
    return !isLargeString(elementText(el))
  }
  // intl
  if (target.provenance.status !== "exact") return false
  const key = target.provenance.key
  if (index?.flags[key] === "template") return false
  const onPage = index?.byKey[key]?.[focusLocale] ?? elementText(el)
  return !isLargeString(onPage)
}

export interface InlineEditSession {
  destroy: () => void
}

/**
 * Turn `el` into a plain-text contentEditable edit session. Keystrokes →
 * `onInput`; plain Enter → `onEnter` (and the newline is suppressed); Esc →
 * `onEscape`; focus loss → `onBlur`. Shift+Enter inserts a newline. Paste is
 * coerced to plain text. `destroy()` removes listeners before blurring/clearing
 * so it never re-fires `onBlur`.
 */
export function createInlineEditSession({
  el,
  onInput,
  onEnter,
  onEscape,
  onBlur,
}: {
  el: HTMLElement
  onInput: (text: string) => void
  onEnter: () => void
  onEscape: () => void
  onBlur: () => void
}): InlineEditSession {
  // plaintext-only avoids rich-text DOM (<div>/<br>) from Enter; fall back to
  // "true" where unsupported (older Firefox). Use setAttribute so the content
  // attribute reflects (some engines don't mirror the IDL setter to the attr).
  el.setAttribute("contenteditable", "plaintext-only")
  if (el.contentEditable !== "plaintext-only" && el.contentEditable !== "")
    el.setAttribute("contenteditable", "true")
  el.spellcheck = false
  const prevOutline = el.style.outline
  el.style.outline = "2px solid #2b6cff"
  el.style.outlineOffset = "2px"

  el.focus()
  // caret to end
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(false)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)

  function handleInput() {
    onInput(el.textContent ?? "")
  }
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      onEnter()
    } else if (e.key === "Escape") {
      e.preventDefault()
      e.stopPropagation()
      onEscape()
    }
  }
  function handlePaste(e: ClipboardEvent) {
    e.preventDefault()
    const text = e.clipboardData?.getData("text/plain") ?? ""
    el.ownerDocument.execCommand("insertText", false, text)
  }
  function handleBlur() {
    onBlur()
  }

  el.addEventListener("input", handleInput)
  el.addEventListener("keydown", handleKeydown)
  el.addEventListener("paste", handlePaste)
  el.addEventListener("blur", handleBlur)

  return {
    destroy() {
      el.removeEventListener("input", handleInput)
      el.removeEventListener("keydown", handleKeydown)
      el.removeEventListener("paste", handlePaste)
      el.removeEventListener("blur", handleBlur)
      el.removeAttribute("contenteditable")
      el.style.outline = prevOutline
      el.style.removeProperty("outline-offset")
    },
  }
}
